import { SupportedLocales } from "../i18n/config";
import { getCountryLanguageFromLocaleCode } from "./i18n";

export interface PageNode {
  id: string;
  contentful_id: string;
  slug: string | null;
  node_locale: string;
  parentPage?: { id: string } | null;
}

export function getNodeAncestors<
  T extends Pick<Queries.StandardPageDataFragment, "id" | "parentPage">,
>(currentNode: T, nodes: Record<string, T>, fromRoot = true): T[] {
  const parentPage = nodes[currentNode?.parentPage?.id ?? ""] ?? null;

  const nodesList = [
    currentNode,
    ...(parentPage ? getNodeAncestors(parentPage, nodes, false) : []),
  ].filter(Boolean);

  if (fromRoot) nodesList.reverse();

  return nodesList;
}

export function getNodeFullPath<
  T extends Pick<Queries.StandardPageDataFragment, "slug" | "node_locale" | "id" | "parentPage">,
>(node: T, nodes: Record<string, T>) {
  const fullSlug = getNodeAncestors(node, nodes)
    .map((node) => node.slug ?? "") // should never be null once published (slug is required)
    .filter(Boolean)
    .join("/");

  return getLocalizedPath(fullSlug, node.node_locale);
}

export function getLocalizedPath(
  path: string,
  localeCode: string | SupportedLocales,
  baseUrl = ""
) {
  const { country, language } = getCountryLanguageFromLocaleCode(localeCode);
  return baseUrl + `/${country}/${language}/${path}`.replace(/(\/)+/g, "/"); // replace double slashes
}

export function getNodesMap<T extends Pick<Queries.StandardPageDataFragment, "id">>(
  pageNodes: T[]
) {
  return pageNodes.reduce<Record<string, T>>(
    (acc, node) => ({
      ...acc,
      [node.id]: node,
    }),
    {}
  );
}

export function getAlternateUrlsMap<
  T extends Pick<
    Queries.StandardPageDataFragment,
    "contentful_id" | "node_locale" | "id" | "parentPage" | "slug"
  >,
>(pageNodesMap: Record<string, T>, baseUrl: string) {
  return Object.values(pageNodesMap).reduce<Record<string, Record<string, string>>>(
    (acc, node) => ({
      ...acc,
      [node.contentful_id]: {
        ...(acc[node.contentful_id] ?? {}),
        [node.node_locale]: baseUrl + getNodeFullPath(node, pageNodesMap),
      },
    }),
    {}
  );
}
